import { assetsMediaContentRetrieve } from '@goldcast/api/content';

import store from '@/store';

// Mirror of content-studio's READINESS_SHOW_STATUS_EVENT (constants cannot cross the MF boundary).
export const READINESS_SHOW_STATUS_EVENT = 'cl:readiness-show-status';

// Shown when a user opens a still-processing upload from the video library. The shell readiness popup
// auto-dismisses the PROCESSING phase, so this toast is the only feedback the user gets. FAILED keeps
// its popup (that phase is not auto-dismissed), so it is deliberately excluded here.
export const UPLOAD_PROCESSING_TOAST_MESSAGE =
  "This video is still processing. You'll be able to access it once processing is complete.";

export interface UploadReadinessGateInput {
  contentId: string;
  projectId?: string;
  title?: string;
}

/**
 * Outcome of the click→API gate:
 *  - `EDITOR_READY` → `upload_editor_status === 'DONE'`; open the editor directly. The caller should
 *                     route straight to the editor even if its transcode-based edit URL still points
 *                     at the legacy `/import` page (transcript can be ready before HLS finishes).
 *  - `OPEN_LEGACY`  → no `upload_editor_status` (content created before the field existed); nothing
 *                     to gate on, so open via the caller's existing routing (backward compatibility).
 *  - `NOT_READY`    → `PROCESSING`/`FAILED`; the popup was shown the current status; do not open.
 *  - `CHECK_FAILED` → the readiness read errored; the caller should toast and not open.
 */
export type UploadEditorGateOutcome = 'EDITOR_READY' | 'OPEN_LEGACY' | 'NOT_READY' | 'CHECK_FAILED';

/**
 * Universal click→API gate (spec §4.2). Reads the durable `upload_editor_status` from the media
 * content API and decides whether opening the editor is safe.
 *
 * Fails *closed* (`CHECK_FAILED`) on an API error: the new flow has no legacy `/import` processing
 * page to fall back to, so opening a not-ready editor would strand the user. The caller surfaces a
 * "couldn't check status" toast instead so the click isn't a silent no-op.
 */
export async function gateUploadEditorEntry(input: UploadReadinessGateInput): Promise<UploadEditorGateOutcome> {
  try {
    const media = await assetsMediaContentRetrieve({ id: input.contentId });
    if (media.upload_editor_status === 'DONE') {
      return 'EDITOR_READY';
    }
    // Legacy content created before `upload_editor_status` existed: the API omits the field, so
    // there is nothing to gate on. Fall back to the caller's existing routing (backward compat).
    // A falsy check catches both `null` and `undefined`.
    if (!media.upload_editor_status) {
      return 'OPEN_LEGACY';
    }
    document.dispatchEvent(
      new CustomEvent(READINESS_SHOW_STATUS_EVENT, {
        detail: {
          content_id: input.contentId,
          project_id: input.projectId,
          title: input.title,
          upload_editor_status: media.upload_editor_status,
          batch_status: media.batch_status ?? null
        }
      })
    );
    // The readiness popup auto-dismisses PROCESSING, so surface a warning toast as the user's feedback.
    if (media.upload_editor_status === 'PROCESSING') {
      store.dispatch('studioToast/triggerToast', {
        mode: 'warning',
        msg: UPLOAD_PROCESSING_TOAST_MESSAGE
      });
    }
    return 'NOT_READY';
  } catch {
    return 'CHECK_FAILED';
  }
}
